import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  Card,
  Group,
  Text,
  Image,
  Button,
  Progress,
  Alert,
  Stack,
  Tooltip,
  Badge,
} from "@mantine/core";
import {
  IconTrash,
  IconApps,
  IconCalendar,
  IconEdit,
} from "@tabler/icons-react";

import { BobNostrConnection } from "../../stores/BobNostrConnection";
import { Bob } from "../../stores/Bob";
import { abbreviateNumber } from "../../../helpers/abbreviate";
import { BOB_MODALS } from "../../helpers/constants";

const BobNostrConnectionCard = observer(
  ({ connection, bob }: { connection: BobNostrConnection; bob: Bob }) => {
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

    /* ---------- derived values ---------- */
    const displayName = connection.name || connection.uuid.slice(0, 6);
    const hasBudget = connection.budget_amount_sats !== null;
    const budgetLeft = connection.total_amount_left_in_budget ?? 0;

    const budgetUsed = hasBudget
      ? Math.min(
          100,
          ((connection.budget_amount_sats - budgetLeft) /
            connection.budget_amount_sats) *
            100
        )
      : 0;

    const formatBudget = (value: number) => abbreviateNumber(value, 1);

    /* ---------- expiration-date logic ---------- */
    let expirationDate: Date | null = null;
    if (
      connection.connection_expiration_days &&
      connection.connection_expiration_measured_from_date
    ) {
      const start = new Date(
        connection.connection_expiration_measured_from_date
      );
      expirationDate = new Date(start);
      expirationDate.setDate(
        start.getDate() + connection.connection_expiration_days
      );
    }

    /* ---------- actions ---------- */
    const handleDelete = () => {
      bob.updateNostrConnections("delete-one", connection.uuid);
    };

    return (
      <Card shadow="sm" padding="md" radius="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Image
              src="https://daxwktpz4hkwd.cloudfront.net/nwc_logos/nwc-square-color-two.png"
              alt="NWC Logo"
              w={20}
              h={20}
              fit="contain"
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Text span fw={700} lh={1.25} style={{ minWidth: 0 }}>
              {displayName}
            </Text>
          </Group>

          {!isConfirmingDelete ? (
            <>
              {/* primary actions */}
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="filled"
                  color="green"
                  leftSection={<IconApps size={14} />}
                  onClick={() => {
                    bob.selectNostrConnection(connection);
                    connection.setEditorialViewStatus("show");
                    bob.setOpenModal(BOB_MODALS.SINGLE_NOSTR_CONNECTION);
                  }}
                >
                  Get Connection Code
                </Button>

                <Button
                  size="xs"
                  variant="default"
                  leftSection={<IconEdit size={14} />}
                  onClick={() => {
                    bob.selectNostrConnection(connection);
                    connection.setEditorialViewStatus("edit");
                    bob.setOpenModal(BOB_MODALS.SINGLE_NOSTR_CONNECTION);
                  }}
                >
                  Edit
                </Button>

                <Button
                  size="xs"
                  variant="default"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => setIsConfirmingDelete(true)}
                >
                  Delete
                </Button>
              </Group>

              {/* budget / warning */}
              {hasBudget ? (
                <Progress.Root size={18} mt="xs" mb="xs" radius="md">
                  <Progress.Section value={budgetUsed} color="#857d7d" />
                  <Progress.Section value={100 - budgetUsed} color="blue">
                    <Progress.Label
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "clip",
                        opacity: 0.79,
                      }}
                    >
                      {formatBudget(budgetLeft)} sats remaining
                    </Progress.Label>
                  </Progress.Section>
                </Progress.Root>
              ) : (
                <Alert color="red" mt="md">
                  No budget is set. This connection could quickly spend all your
                  outbound funds.
                </Alert>
              )}

              {/* expiration badge */}
              {expirationDate && (
                <Badge
                  size="lg"
                  color="var(--mantine-color-gray-8)"
                  leftSection={<IconCalendar size={14} />}
                >
                  Expires {expirationDate.toLocaleDateString()}
                </Badge>
              )}
            </>
          ) : (
            /* delete-confirmation branch */
            <Stack>
              <Alert color="red" title="Confirm Deletion" mt="md">
                Are you sure you want to delete this? Inbound payments using
                this connection will fail.
              </Alert>
              <Group gap="xs" mt="sm">
                <Button
                  size="xs"
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={handleDelete}
                >
                  Yes, Delete
                </Button>
                <Button
                  size="xs"
                  color="blue"
                  onClick={() => setIsConfirmingDelete(false)}
                >
                  Cancel
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </Card>
    );
  }
);

export default BobNostrConnectionCard;
